import './main.scss';

import { filter, find, reject } from 'lodash';

import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { Barcodes } from '@pos/app/scripts/services/barcodes';
import { Broadcaster } from '@core/app/scripts/services/broadcaster';
import { CashierSession } from '@shared/app/scripts/services/cashier-session';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { Domain } from '@shared/app/scripts/services/domain';
import { EndOfShift } from '@pos/app/scripts/services/end-of-shift';
import { LastTransaction } from '@pos/app/scripts/services/last-transaction';
import { LocationManager } from '@shared/app/scripts/services/location-manager';
import { Persist } from '@core/app/scripts/services/persist';
import { PosState } from '@pos/app/scripts/services/pos-state';
import { SalesManager } from '@shared/app/scripts/services/sales-manager';
import { Session } from '@shared/app/scripts/services/session';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { UserSession } from '@core/app/scripts/services/user-session';
import { Utility } from '@core/app/scripts/services/utility';
import { themeManager } from '../../services/theme-manager';

/*
 * Component responsible for /main route.
 */
angular
  .module('posApp')
  .directive(
    'main',
    (
      $injector: ng.IInjectStatic,
      $log: ng.ILogService,
      $window: ng.IWindowService,
      SaleModel,
      barcodes: Barcodes,
      broadcaster: Broadcaster,
      cashierManager,
      cashierSession: CashierSession,
      displayMessage: DisplayMessage,
      domain: Domain,
      endOfShift: EndOfShift,
      intercom,
      lastTransaction: LastTransaction,
      legacyPrint,
      locationManager: LocationManager,
      md5,
      navigateTo: NavigateTo,
      paymentTypes,
      persist: Persist,
      pinProvider,
      pos,
      posInstance,
      posState: PosState,
      printLastReceipt,
      printReceipt,
      retailTransactions,
      salesManager: SalesManager,
      session: Session,
      settingsManager: SettingsManager,
      salesCalculations,
      translate: ng.translate.ITranslateService,
      userSession: UserSession,
      utility: Utility,
    ) => ({
      restrict: 'E',

      scope: true,

      template: require('./main.html').default,

      link: function ($scope: any) {
        const $translate = translate,
          mainInit = $injector.get('mainInit');

        cashierSession.reset();

        // http://jira.softwear.nl:8080/browse/SWUI-862
        // Make yourself known to possible other POS invocations

        const helloUuid = utility.generateRandomAlphaNumeric(16);

        // TODO make this logic more straightforward
        // 1) Hello is anybody there
        persist.set(
          'hello',
          angular.toJson({
            Iam: 'POS',
            // The UUID below doesn't realy do anything other than making sure that the localStorage gets updated.
            // Then and only then will the 'storage' event be fired in other browsers
            uuid: helloUuid,
          }),
        );

        // This will be fired if other browser update a value in localStorage
        $window.addEventListener(
          'storage',
          (e) => {
            const v = angular.fromJson(e.newValue);
            // 2) Yes, I've been here before
            if (e.key == 'hello' && v.Iam == 'POS') {
              persist.set(
                'iwasherefirst',
                angular.toJson({
                  Iam: 'POS',
                  uuid: utility.generateRandomAlphaNumeric(16),
                  helloUuid,
                }),
              );
            }
            // 3) OK, you've been first, so I leave
            if (
              e.key == 'iwasherefirst' &&
              v.Iam == 'POS' &&
              v.helloUuid !== helloUuid
            ) {
              const redirect = () => {
                $window.location = 'http://' + domain.getParent();
                return false;
              };

              displayMessage.message({
                message: $translate.instant('POSAlreadyRunning'),
                callback: redirect,
                type: 'errorprompt',
                ok: true,
              });
            }
          },
          false,
        );

        $scope.pincode = '';
        $scope.switchUserFlag = false;
        //Controls the ability to see the pinpad
        $scope.dataLoaded = false;
        $scope.lastTransactionAmount = 0;
        $scope.firstEnable = true;
        $scope.unitTest = false;

        // Theme management
        $scope.isLightMode = themeManager.isLightMode();
        $scope.toggleTheme = () => {
          themeManager.toggleTheme();
          $scope.isLightMode = themeManager.isLightMode();
        };

        /**
         * @ngdoc
         * @methodOf posApp.mainController
         * @name posApp.mainController#openLastReceiptPopup
         * @description
         * Open the last receipt popup
         */
        // TODO cover with test
        $scope.openLastReceiptPopup = () => {
          const transaction = lastTransaction.get();

          $scope.lastTransactionAmount = 0;
          $scope.lastTransactionCashback = 0;
          $scope.email = '';

          if (transaction) {
            $scope.lastTransactionAmount = salesCalculations.total(transaction);

            const cashbackCoupon = find(transaction.coupon, { type: 'C' });
            if (cashbackCoupon) {
              $scope.lastTransactionCashback = cashbackCoupon.value;
            }

            if (transaction.customer) {
              $scope.email = transaction.customer.email;
            }
          }

          $scope.lastReceiptPopup = true;
        };

        /*
         * Fires when critical startup data cannot be obtained
         */
        // TODO remove the method form $scope
        const loadFailure = ($scope.loadFailure = (message) => {
          if (!message) {
            message = $translate.instant('ThereWasAnError');
          } else {
            message = $translate.instant(message);
          }

          $log.error(message);

          // If there is a load failure, send the user back to www
          // TODO write a shared service for this operation
          $window.location.href = domain.link(
            `www#/home?error=${encodeURIComponent(message)}`,
          );
        });

        function navigationHandler(view) {
          navigateTo(view);
          // Hide intercom when changing page
          intercom.update({ hide_default_launcher: true });
        }

        broadcaster.send('setFocus', 'posMainPincode');

        // Show intercom chat when PIN screen loads
        intercom.update({ hide_default_launcher: false });

        if (settingsManager.settings.email.autoPopup && lastTransaction.get()) {
          $scope.openLastReceiptPopup();
        }

        session.current$.subscribe((currentSession) => {
          $scope.userFullName = currentSession.fullname;
        });

        $scope.barcodes = barcodes;

        $scope.emailActive = settingsManager.settings.email.active;

        /**
         * @ngdoc
         * @name posApp.mainController#enablePINPad
         * @methodOf posApp.mainController
         * @description
         * Checks if all initialization has been loaded and enables the pin pad used
         * by the cashier to log in to the POS.
         * @returns {boolean} enable
         */
        $scope.enablePINPad = () => {
          if (
            barcodes.loaded &&
            posInstance.loaded &&
            locationManager.loaded &&
            cashierManager.loaded &&
            endOfShift.loaded
          ) {
            //Only broadcast this once when the pinpad field first becomes visable SWUI-1105
            if ($scope.firstEnable) {
              $scope.firstEnable = false;
              broadcaster.send('setFocus', 'posMainPincode');
            }
            return true;
          }
          return false;
        };

        $scope.onChangeCashier = () => {
          pos.onChangeCashier();
        };

        /**
         * @description
         * Used to reload a transaction that was not completed in the
         * previous session. Also handles reloading a unfinished PayPlazaPIN Transaction.
         * This method should only be invoked once per login session.
         */
        const loadTransaction = () => {
          let transaction,
            serializedTransaction = persist.get('posTransaction');

          if (
            settingsManager.settings.pos?.saveTwicePattern === true &&
            serializedTransaction
          ) {
            persist.remove('posTransaction');
            serializedTransaction = null;
          }

          // Check for existing transaction or create a new transaction
          if (serializedTransaction) {
            transaction = JSON.parse(serializedTransaction);

            transaction.receipt = reject(transaction.receipt, { found: false });

            if (transaction.type === 3) {
              pos.transaction = new SaleModel(transaction);

              navigateTo('pos.route#checkout-transit-order');
              return;
            }

            /*
             * Check the PayPlazaPIN service for an outstanding transaction on login
             * only when the PIN key is set and the pin payment uses the coupled web service version
             */
            if (
              settingsManager.settings.pinId &&
              paymentTypes.rules.pin.coupled === true
            ) {
              /*
               * This is a PIN transaction, the user must complete before continuing
               * use the transaction directly from localStorage
               */
              pos.transaction = new SaleModel(transaction);

              pinProvider.init();

              if (settingsManager.settings.pinProvider === 'payPlaza4') {
                pinProvider.checkServer(pos.transaction).then(
                  (result) => {
                    displayMessage.message({
                      message: $translate.instant('Reloading PIN Transaction'),
                      type: 'info',
                    });
                    navigationHandler('pos.route#checkout');

                    result.pickedUpTransactionPromise.then(
                      (printOut) => {
                        if (printOut) {
                          pos.transaction.pinInfo += printOut;
                        }

                        pos.setTransactionProperties(
                          pos.transaction,
                          salesManager.counters.sales,
                        );

                        printReceipt().then(pos.sendReceipt, pos.sendReceipt);
                      },
                      (errorPrintOut) => {
                        if (errorPrintOut) {
                          pos.transaction.pinError += errorPrintOut;
                        }
                        pos.transaction.payments.pin = 0.0;

                        //Update all totals
                        pos.updateTotals(pos.transaction);
                      },
                    );
                  },
                  (resp) => {
                    console.log(
                      '[POSMain] No outstanding PIN transactions',
                      resp,
                    );
                    // Navigate to POS view
                    navigationHandler('pos.route#pos');
                  },
                );
              } else {
                // payNl, ignores check server step
                navigationHandler('pos.route#pos');
              }
            } else {
              // TODO add unit test
              if (transaction.return_original_nr) {
                retailTransactions
                  .filter({ nr: transaction.return_original_nr })
                  .then((response) => {
                    if (response && response.length) {
                      posState.returnTransaction = angular.copy(response[0]);
                    }
                  });
              }

              pos.reloadTransaction(transaction);

              // Navigate to POS view
              navigationHandler('pos.route#pos');
            }
          } else {
            // There is no pending transaction to load
            navigationHandler('pos.route#pos');
          }
        };

        /**
         * @ngdoc
         * @name posApp.mainController#pinCodeHandler
         * @methodOf posApp.mainController
         * @description
         * The login handler for the cashier pin code entry
         */
        $scope.pinCodeHandler = () => {
          const result = $scope.authCashier();

          if (result) {
            $scope.pincode = '';
            /*
             * Only attempt to reload a transaction the first time the pin pad is used
             * The reason for this is we need all of the settings loaded and also a
             * different cashier may complete a pending transaction
             */
            if (mainInit.firstTime) {
              mainInit.firstTime = false;
              loadTransaction();
            } else {
              //Show POS
              navigationHandler('pos.route#pos');
            }
          }
        };

        /**
         * @ngdoc
         * @methodOf posApp.mainController
         * @name posApp.mainController#changeCashier
         * @description
         * This method allows a cashier to switch with another cashier and updates
         * the `pos.config.selectedCashier` object as well as changing the
         * `pos.transaction.cashier` object. Also the `pos.config.acls` object is
         * updated to match the new cashier's ACLs object.
         * @param {string} _pwd the passcode of the cashier.
         */
        function changeCashier(_pwd) {
          _pwd = $scope.pwdHash(_pwd);
          const cashier = filter(cashierManager.list, {
            password_hash: _pwd,
          });

          //If Cashier found return true
          if (cashier.length > 0) {
            // TODO remove selectedCashier form pos.config
            pos.config.selectedCashier = cashier[0];

            cashierSession.set(cashier[0]);

            pos.config.acls = angular.copy(pos.config.selectedCashier.acls);
            userSession.set({
              cashierACLs: pos.config.acls,
            });

            //Must update the transaction to reflect the change of cashier
            pos.setCashier(pos.transaction, pos.config.selectedCashier);

            return;
          }

          const message = $translate.instant('WrongCode');
          displayMessage.message(message);
        }

        /**
         * @ngdoc
         * @methodOf posApp.mainController
         * @name posApp.mainController#authCashier
         * @description
         * This method manipulates directly the `pincode` ng-model removing any extras
         * hash characters from scanned passcodes.
         * @returns {boolean} authed true in the cashier is authorised, false if the
         * passcode is not correct.
         */
        $scope.authCashier = () => {
          //Remove # from pincode SWUI-965
          $scope.pincode = $scope.pincode.replace('#', '');

          if ($scope.checkCashier($scope.pincode)) {
            changeCashier($scope.pincode);
            return true;
          }
          return false;
        };

        /**
         * @ngdoc
         * @methodOf posApp.mainController
         * @name posApp.mainController#checkCashier
         * @description
         * This is a helper method of authCashier.
         * @param {string} _pwd the passcode of the cashier
         * It authenticates the password of a cashier again their password hash.
         */
        $scope.checkCashier = (_pwd) => {
          _pwd = $scope.pwdHash(_pwd);
          const cashier = filter(cashierManager.list, {
            password_hash: _pwd,
          });

          //If Cashier found return true
          if (cashier.length > 0) {
            return true;
          } else {
            const message = $translate.instant('WrongCode');
            displayMessage.message(message);
            return false;
          }
        };

        $scope.pwdHash = (hashText) => md5(hashText);

        /**
         * @ngdoc
         * @methodOf posApp.mainController
         * @name posApp.mainController#onKeyPress
         * @description
         * Deprecated - This method wraps the `pos.key` function which virtually types
         * a value into a specific ng-model. Replaced by the `softwear.shared` keypad
         * directive.
         */
        $scope.onKeyPress = (val) => {
          pos.key(val, $scope);
          return;
        };

        /**
         * @ngdoc
         * @methodOf posApp.mainController
         * @name posApp.mainController#closeLastReceiptPopup
         * @description
         * Close the last receipt popup
         */
        // TODO cover with test
        $scope.closeLastReceiptPopup = () => {
          $scope.lastReceiptPopup = false;
          $scope.email = '';
          broadcaster.send('setFocus', 'posMainPincode');
        };

        // TODO cover with test
        $scope.hasLastReceipt = !!angular.isObject(lastTransaction.get());

        /**
         * @ngdoc
         * @methodOf posApp.mainController
         * @name posApp.mainController#reprintLastReceipt
         * @description
         * Reprints the last receipt
         */
        // TODO cover with test
        $scope.reprintLastReceipt = () => {
          printLastReceipt().then(angular.noop, () => {
            displayMessage.message($translate.instant('PrintingFailed'));
          });

          $scope.lastReceiptPopup = false;
          broadcaster.send('setFocus', 'posMainPincode');
        };

        /**
         * @ngdoc
         * @methodOf posApp.mainController
         * @name posApp.mainController#emailLastReceipt
         * @param {String} email address of the client
         * @description
         * Send the last receipt to the email
         */
        // TODO cover with test
        $scope.emailLastReceipt = (email) => {
          const transaction = lastTransaction.get();

          if (transaction && transaction._id) {
            $scope.emailingReceipt = true;
            legacyPrint.emailReceipt(transaction._id, email).then(
              () => {
                $scope.lastReceiptPopup = $scope.emailingReceipt = false;
                $scope.email = '';
                broadcaster.send('setFocus', 'posMainPincode');
              },
              () => {
                displayMessage.message(
                  $translate.instant('SendingEmailFailed'),
                );
                $scope.lastReceiptPopup = $scope.emailingReceipt = false;
                broadcaster.send('setFocus', 'posMainPincode');
              },
            );
          } else {
            displayMessage.message($translate.instant('LastReceiptNotFound'));
            $scope.lastReceiptPopup = false;
            broadcaster.send('setFocus', 'posMainPincode');
          }
        };
      },
    }),
  );
